import { useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Label, Select } from '../components/ui'
import { ErrorBoundary } from '../components/ErrorBoundary'
import { UserLocationMap } from '../components/locations/UserLocationMap'
import { useCompany } from '../hooks/useCompany'
import { useAuth } from '../hooks/useAuth'
import { useSentryBreadcrumb } from '../hooks/useSentryBreadcrumb'
import { getCurrentLocation, requestLocationPermission } from '../utils/geolocation'
import { api, logger } from '../utils/api'

interface ClockStatus {
  isClockedIn: boolean
  currentEntry: {
    id: string
    clockInAt: string
    locationId: string | null
    methodIn: string
    meta: any
  } | null
}

interface BreakStatus {
  isOnBreak: boolean
  currentBreak: {
    id: string
    startAt: number
    durationMinutes: number
    breakType: string
  } | null
}

interface Location {
  id: string
  team_id: string
  name: string
  restrict_to_location_members: boolean
  created_at: number
}

export default function TimeClock() {
  const { currentCompany } = useCompany()
  const { } = useAuth()
  const { addClickBreadcrumb, addBreadcrumb } = useSentryBreadcrumb()
  const [clockStatus, setClockStatus] = useState<ClockStatus | null>(null)
  const [breakStatus, setBreakStatus] = useState<BreakStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [todayHours, setTodayHours] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [locations, setLocations] = useState<Location[]>([])
  const [selectedLocationId, setSelectedLocationId] = useState<string>('')
  const [wifiVerifying, ] = useState(false)
  const [currentTeamId, setCurrentTeamId] = useState<string | null>(null)
  const [locationPermission, setLocationPermission] = useState<PermissionState | null>(null)
  const [currentUserLocation, setCurrentUserLocation] = useState<{
    latitude: number
    longitude: number
    accuracy?: number
  } | null>(null)

  // Fetch teams for current company
  useEffect(() => {
    const fetchTeamsAndSetCurrent = async () => {
      if (!currentCompany?.id) {
        setCurrentTeamId(null)
        setLoading(false)
        return
      }

      try {
        logger.debug('Fetching teams for company', { companyId: currentCompany.id })
        const data = await api.get(`/v1/teams?company_id=${currentCompany.id}`)
        const teams = data.teams || []
        
        // Find a team where the user is a member (user_role is not null)
        const userTeam = teams.find((team: any) => team.user_role !== null)
        
        if (userTeam) {
          setCurrentTeamId(userTeam.id)
        } else if (teams.length > 0) {
          // Fallback to first team if no membership found
          setCurrentTeamId(teams[0].id)
        } else {
          setCurrentTeamId(null)
          setError('No teams found in this company')
        }
      } catch (err) {
        logger.error('Failed to fetch teams', { error: err })
        setCurrentTeamId(null)
        setError('Failed to load teams')
      }
    }

    fetchTeamsAndSetCurrent()
  }, [currentCompany?.id])

  // Fetch clock status, locations, and today's hours
  useEffect(() => {
    if (!currentTeamId) {
      return
    }

    fetchClockStatus()
    fetchBreakStatus()
    fetchTodayHours()
    fetchUserLocations()
    
    // Check location permission status and get current location
    requestLocationPermission().then(state => {
      setLocationPermission(state)
      if (state === 'granted') {
        // Get initial location
        getCurrentLocation().then(locationData => {
          if (locationData) {
            setCurrentUserLocation({
              latitude: locationData.latitude,
              longitude: locationData.longitude,
              accuracy: locationData.accuracy
            })
          }
        })
      }
    })
  }, [currentTeamId])

  // Update location periodically when clocked in
  useEffect(() => {
    if (!clockStatus?.isClockedIn || locationPermission !== 'granted') return

    const updateLocation = async () => {
      const locationData = await getCurrentLocation()
      if (locationData) {
        setCurrentUserLocation({
          latitude: locationData.latitude,
          longitude: locationData.longitude,
          accuracy: locationData.accuracy
        })
      }
    }

    // Update immediately
    updateLocation()

    // Then update every 30 seconds
    const interval = setInterval(updateLocation, 30000)

    return () => clearInterval(interval)
  }, [clockStatus?.isClockedIn, locationPermission])

  // Update break duration periodically when on break
  useEffect(() => {
    if (!breakStatus?.isOnBreak) return

    // Update every 10 seconds to show updated duration
    const interval = setInterval(() => {
      fetchBreakStatus()
    }, 10000)

    return () => clearInterval(interval)
  }, [breakStatus?.isOnBreak])

  const fetchClockStatus = async () => {
    if (!currentTeamId) return

    try {
      setError(null)
      const data = await api.get(`/v1/timesheets/status?team_id=${currentTeamId}`)
      setClockStatus(data)
    } catch (err) {
      logger.error('Failed to fetch clock status', { error: err })
      setError(err instanceof Error ? err.message : 'Failed to load status')
    } finally {
      setLoading(false)
    }
  }

  const fetchBreakStatus = async () => {
    try {
      const data = await api.get('/v1/timesheets/break-status')
      logger.debug('Break status fetched:', data)
      setBreakStatus(data)
    } catch (err) {
      logger.error('Failed to fetch break status', { error: err })
      // Set default state on error
      setBreakStatus({ isOnBreak: false, currentBreak: null })
    }
  }

  const fetchTodayHours = async () => {
    if (!currentTeamId) return

    try {
      const data = await api.get(`/v1/timesheets/today-hours?team_id=${currentTeamId}`, {
        headers: {
          'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone
        }
      })
      setTodayHours(data.totalHours || 0)
    } catch (err) {
      logger.error('Failed to fetch today hours', { error: err })
    }
  }

  const fetchUserLocations = async () => {
    if (!currentTeamId) return

    try {
      const data = await api.get(`/v1/locations?team_id=${currentTeamId}&user_accessible=true`)
      setLocations(data.locations || [])
      // Set default location if only one available
      if (data.locations?.length === 1) {
        setSelectedLocationId(data.locations[0].id)
      }
    } catch (err) {
      logger.error('Failed to fetch locations', { error: err })
    }
  }




  const handleClockIn = async (method: 'FREE' | 'GEO' | 'QR' | 'PHOTO') => {
    if (!currentTeamId) return

    // Add breadcrumb for clock in action
    addClickBreadcrumb('clock-in-button', 'clock_in', {
      method,
      locationId: selectedLocationId || 'none',
      teamId: currentTeamId
    })

    // Create a Sentry transaction for clock-in
    return Sentry.startSpan(
      {
        op: 'ui.action.clock_in',
        name: 'Clock In',
        attributes: {
          method,
          teamId: currentTeamId,
          locationId: selectedLocationId || 'none'
        }
      },
      async () => {
        try {
          setSubmitting(true)
          setError(null)
          
          logger.info('Starting clock in', { method, teamId: currentTeamId })

      // WiFi verification disabled until native app is available
      // if (requiresWifiVerification('CLOCK_IN')) {
      //   const wifiVerified = await handleWifiVerification('clock_in')
      //   if (!wifiVerified) {
      //     return
      //   }
      // }

      // Always try to get current location, regardless of method
      const locationData = await getCurrentLocation()
      let meta: any = {}
      
      if (locationData) {
        meta.latitude = locationData.latitude
        meta.longitude = locationData.longitude
        meta.accuracy = locationData.accuracy
        meta.timestamp = locationData.timestamp
        
        // Update current location state
        setCurrentUserLocation({
          latitude: locationData.latitude,
          longitude: locationData.longitude,
          accuracy: locationData.accuracy
        })
      }
      
      // If GEO method is used and location failed, show error
      if (method === 'GEO' && !locationData) {
        setError('Location access denied or unavailable. Please enable location services.')
        return
      }

      await api.post('/v1/timesheets/clock-in', {
        teamId: currentTeamId,
        locationId: selectedLocationId || undefined,
        method,
        meta
      })

      // Refresh status
      await fetchClockStatus()
      await fetchBreakStatus()
      await fetchTodayHours()
      
      // Add success breadcrumb
      addBreadcrumb('Clock in successful', {
        method,
        teamId: currentTeamId,
        locationId: selectedLocationId || 'none'
      }, 'user_action', 'info')
    } catch (err) {
      logger.error('Clock in failed', { error: err, method, teamId: currentTeamId })
      setError(err instanceof Error ? err.message : 'Failed to clock in')
    } finally {
      setSubmitting(false)
    }
      }
    )
  }

  const handleClockOut = async (method: 'FREE' | 'GEO' | 'QR' | 'PHOTO') => {
    if (!currentTeamId || !clockStatus?.currentEntry) return

    // Add breadcrumb for clock out action
    addClickBreadcrumb('clock-out-button', 'clock_out', {
      method,
      teamId: currentTeamId
    })

    // Create a Sentry transaction for clock-out
    return Sentry.startSpan(
      {
        op: 'ui.action.clock_out',
        name: 'Clock Out',
        attributes: {
          method,
          teamId: currentTeamId
        }
      },
      async () => {
        try {
          setSubmitting(true)
          setError(null)

      // WiFi verification disabled until native app is available
      // if (requiresWifiVerification('CLOCK_OUT')) {
      //   const wifiVerified = await handleWifiVerification('clock_out')
      //   if (!wifiVerified) {
      //     return
      //   }
      // }

      // Always try to get current location, regardless of method
      const locationData = await getCurrentLocation()
      let meta: any = {}
      
      if (locationData) {
        meta.latitude = locationData.latitude
        meta.longitude = locationData.longitude
        meta.accuracy = locationData.accuracy
        meta.timestamp = locationData.timestamp
        
        // Update current location state
        setCurrentUserLocation({
          latitude: locationData.latitude,
          longitude: locationData.longitude,
          accuracy: locationData.accuracy
        })
      }
      
      // If GEO method is used and location failed, show error
      if (method === 'GEO' && !locationData) {
        setError('Location access denied or unavailable. Please enable location services.')
        return
      }

      await api.post('/v1/timesheets/clock-out', {
        method,
        meta
      })

      // Refresh status
      await fetchClockStatus()
      await fetchBreakStatus()
      await fetchTodayHours()
      
      // Add success breadcrumb
      addBreadcrumb('Clock out successful', {
        method,
        teamId: currentTeamId
      }, 'user_action', 'info')
    } catch (err) {
      logger.error('Clock out failed', { error: err, method, teamId: currentTeamId })
      setError(err instanceof Error ? err.message : 'Failed to clock out')
    } finally {
      setSubmitting(false)
    }
      }
    )
  }

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  const handleStartBreak = async () => {
    if (!clockStatus?.isClockedIn) return

    // Add breadcrumb for break start
    addClickBreadcrumb('start-break-button', 'start_break', {})

    try {
      setSubmitting(true)
      setError(null)
      
      logger.info('Starting break')

      const response = await api.post('/v1/timesheets/start-break', {
        breakType: 'REGULAR'
      })
      
      logger.debug('Start break response:', response)

      // Refresh status
      await fetchBreakStatus()
      
      // Add success breadcrumb
      addBreadcrumb('Break started successfully', {}, 'user_action', 'info')
    } catch (err) {
      logger.error('Start break failed', { error: err })
      setError(err instanceof Error ? err.message : 'Failed to start break')
    } finally {
      setSubmitting(false)
    }
  }

  const handleEndBreak = async () => {
    if (!breakStatus?.isOnBreak) return

    // Add breadcrumb for break end
    addClickBreadcrumb('end-break-button', 'end_break', {})

    try {
      setSubmitting(true)
      setError(null)
      
      logger.info('Ending break')

      const response = await api.post('/v1/timesheets/end-break', {})
      
      logger.debug('End break response:', response)

      // Refresh status
      await fetchBreakStatus()
      await fetchTodayHours()
      
      // Add success breadcrumb
      addBreadcrumb('Break ended successfully', {}, 'user_action', 'info')
    } catch (err) {
      logger.error('End break failed', { error: err })
      setError(err instanceof Error ? err.message : 'Failed to end break')
    } finally {
      setSubmitting(false)
    }
  }

  const formatDuration = (dateString: string) => {
    const start = new Date(dateString)
    const now = new Date()
    const diff = now.getTime() - start.getTime()
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    return `${hours}h ${minutes}m`
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-48 mb-6"></div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="h-96 bg-gray-200 rounded"></div>
            <div className="space-y-6">
              <div className="h-48 bg-gray-200 rounded"></div>
              <div className="h-48 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!currentCompany) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <p className="text-gray-600">Please select a company to use the time clock.</p>
        </div>
      </div>
    )
  }

  const isClockedIn = clockStatus?.isClockedIn || false

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 sm:px-0">
        <h1 className="text-2xl font-semibold text-gray-900">Time Clock</h1>
      </div>

      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Location permission banner */}
      {locationPermission === 'denied' && (
        <div className="mt-4 bg-yellow-50 border border-yellow-200 px-4 py-3 rounded-lg">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-yellow-600 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <div>
              <p className="text-yellow-800 font-medium">Location Access Disabled</p>
              <p className="text-yellow-700 text-sm mt-1">
                Location tracking helps verify your work location. Please enable location access in your browser settings for better time tracking accuracy.
              </p>
            </div>
          </div>
        </div>
      )}

      {locationPermission === 'prompt' && (
        <div className="mt-4 bg-blue-50 border border-blue-200 px-4 py-3 rounded-lg">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-600 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-blue-800 font-medium">Location Access</p>
              <p className="text-blue-700 text-sm mt-1">
                We automatically capture your location when you clock in/out to help verify your work location and ensure accurate time tracking.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ErrorBoundary section="clock-in-out">
          <Card>
            <CardHeader>
              <CardTitle>Clock In/Out</CardTitle>
              <CardDescription>Select a method to {isClockedIn ? 'clock out' : 'clock in'}</CardDescription>
            </CardHeader>
            <CardContent>
            <div className="space-y-4">
              {!isClockedIn && locations.length > 0 && (
                <div>
                  <Label htmlFor="location-select">Select Location</Label>
                  <Select
                    id="location-select"
                    value={selectedLocationId}
                    onChange={(e) => {
                      setSelectedLocationId(e.target.value)
                      addBreadcrumb('Location selected', {
                        locationId: e.target.value,
                        locationName: locations.find(l => l.id === e.target.value)?.name || 'none'
                      }, 'ui.input')
                    }}
                    className="w-full mt-1"
                  >
                    <option value="">No specific location</option>
                    {locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </Select>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <Button
                  size="lg"
                  variant={isClockedIn ? 'danger' : 'primary'}
                  onClick={() => isClockedIn ? handleClockOut('FREE') : handleClockIn('FREE')}
                  disabled={submitting}
                  className="h-24"
                >
                  {submitting ? 'Processing...' : (isClockedIn ? 'Clock Out' : 'Clock In')}
                </Button>
                <Button 
                  key={`break-btn-${breakStatus?.isOnBreak ? 'on' : 'off'}`}
                  size="lg" 
                  variant={breakStatus?.isOnBreak ? 'warning' : 'secondary'} 
                  className="h-24"
                  onClick={breakStatus?.isOnBreak ? handleEndBreak : handleStartBreak}
                  disabled={!isClockedIn || submitting}
                >
                  {submitting ? 'Processing...' : (breakStatus?.isOnBreak ? 'End Break' : 'Start Break')}
                </Button>
              </div>


              <div className="pt-4 border-t">
                <p className="text-sm font-medium text-gray-700 mb-3">Alternative Methods</p>
                <div className="grid grid-cols-2 gap-3">
                  <Button 
                    variant="ghost" 
                    className="justify-start"
                    onClick={() => isClockedIn ? handleClockOut('GEO') : handleClockIn('GEO')}
                    disabled={submitting}
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Location
                  </Button>
                  <Button variant="ghost" className="justify-start" disabled>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                    </svg>
                    QR Code
                  </Button>
                  <Button variant="ghost" className="justify-start" disabled>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Photo
                  </Button>
                  <Button 
                    variant="ghost" 
                    className="justify-start opacity-50 cursor-not-allowed relative"
                    disabled
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                    </svg>
                    WiFi Network
                    <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full">Soon</span>
                  </Button>
                  <Button 
                    variant="ghost" 
                    className="justify-start"
                    onClick={() => isClockedIn ? handleClockOut('FREE') : handleClockIn('FREE')}
                    disabled={submitting || wifiVerifying}
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Manual
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        </ErrorBoundary>

        <div className="space-y-6">
          <ErrorBoundary section="time-status">
            <Card>
              <CardHeader>
                <CardTitle>Current Status</CardTitle>
              </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Status</span>
                  <span className={`text-sm font-semibold ${isClockedIn ? 'text-green-600' : 'text-gray-600'}`}>
                    {isClockedIn ? 'Clocked In' : 'Clocked Out'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Today's Hours</span>
                  <span className="text-sm">{todayHours} hours</span>
                </div>
                {isClockedIn && clockStatus?.currentEntry && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">Clock In Time</span>
                      <span className="text-sm">{formatTime(clockStatus.currentEntry.clockInAt)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">Duration</span>
                      <span className="text-sm">{formatDuration(clockStatus.currentEntry.clockInAt)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">Method</span>
                      <span className="text-sm">{clockStatus.currentEntry.methodIn}</span>
                    </div>
                    {clockStatus.currentEntry.locationId && (
                      <div className="flex justify-between">
                        <span className="text-sm font-medium">Location</span>
                        <span className="text-sm">
                          {locations.find(l => l.id === clockStatus.currentEntry?.locationId)?.name || 'Unknown'}
                        </span>
                      </div>
                    )}
                  </>
                )}
                {breakStatus?.isOnBreak && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">Break Status</span>
                      <span className="text-sm font-semibold text-yellow-600">On Break</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">Break Duration</span>
                      <span className="text-sm">{breakStatus.currentBreak?.durationMinutes || 0} minutes</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Company</span>
                  <span className="text-sm">{currentCompany.name}</span>
                </div>
              </div>
            </CardContent>
          </Card>
          </ErrorBoundary>

          {/* User Location Map */}
          {locationPermission === 'granted' && (
            <ErrorBoundary section="location-map">
              <Card>
                <CardHeader>
                  <CardTitle>Your Location</CardTitle>
                  {currentUserLocation && (
                    <CardDescription>
                      Accuracy: ±{Math.round(currentUserLocation.accuracy || 0)}m
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent className="p-0">
                  <UserLocationMap 
                    userLocation={currentUserLocation || undefined}
                    height="250px"
                    showAccuracy={true}
                  />
                </CardContent>
              </Card>
            </ErrorBoundary>
          )}

          <ErrorBoundary section="quick-actions">
            <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Button variant="ghost" className="w-full justify-start" onClick={() => {
                  addClickBreadcrumb('view-timecards-button', 'navigate', { to: '/time/timecards' })
                  window.location.href = '/time/timecards'
                }}>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  View Timecards
                </Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => {
                  addClickBreadcrumb('view-schedule-button', 'navigate', { to: '/time/schedule' })
                  window.location.href = '/time/schedule'
                }}>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  View Schedule
                </Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => {
                  addClickBreadcrumb('request-pto-button', 'navigate', { to: '/time/pto' })
                  window.location.href = '/time/pto'
                }}>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                  Request Time Off
                </Button>
              </div>
            </CardContent>
          </Card>
          </ErrorBoundary>
        </div>
      </div>
    </div>
  )
}